/**
 * BalanceSheetTab — 5-card balance-sheet view, now rendered through the
 * BentoGrid shell (hover lift, gradient edge, icon badge). Card CONTENT,
 * rows, figures, sign convention and test-ids are unchanged — only the
 * card shell/visual changed.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  Scale,
  TrendingDown,
  Layers,
  Wallet,
  Equal,
} from "lucide-react";

import { BentoGrid, type BentoItem } from "@/components/ui/bento-grid";
import type { AuditMovementSchedule } from "@/features/engagement/audit-movement-schedule";
import { formatSigned } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { EngagementHistoryEntry } from "@/types";

interface OutletContext {
  engagement: EngagementHistoryEntry;
  schedule: AuditMovementSchedule;
}

interface AmountRowProps {
  label: string;
  amount: string;
  currency: string;
  emphasize?: boolean;
  indented?: boolean;
  testId?: string;
}

function AmountRow({
  label,
  amount,
  currency,
  emphasize,
  indented,
  testId,
}: AmountRowProps) {
  const num = Number(amount);
  const isNegative = Number.isFinite(num) && num < 0;
  return (
    <div
      data-testid={testId}
      className={cn(
        "flex items-center justify-between gap-2 py-1.5 text-sm",
        emphasize && "border-t border-border pt-2 font-semibold",
        indented && "pl-6",
      )}
    >
      <span className={cn("text-foreground", indented && "text-muted-foreground")}>
        {label}
      </span>
      <span
        className={cn(
          "font-mono tabular-nums",
          isNegative && "text-destructive",
        )}
      >
        {formatSigned(amount, currency)}
      </span>
    </div>
  );
}

function movementRows(
  group: AuditMovementSchedule["rouAssetCost"],
  currency: string,
  testId: string,
  footer?: React.ReactNode,
) {
  return (
    <div className="space-y-1">
      <AmountRow
        label={group.opening.label}
        amount={group.opening.amount}
        currency={currency}
      />
      {group.movements.map((m) => (
        <AmountRow
          key={m.type}
          label={m.label}
          amount={m.amount}
          currency={currency}
          testId={`${testId}-row-${m.type}`}
        />
      ))}
      <AmountRow
        label={group.closing.label}
        amount={group.closing.amount}
        currency={currency}
        emphasize
      />
      {footer}
    </div>
  );
}

export function BalanceSheetTab() {
  const { t } = useTranslation();
  const { engagement, schedule } = useOutletContext<OutletContext>();
  const currency = engagement.functionalCurrency;
  const iconCls = "h-4 w-4";

  const items: BentoItem[] = [
    {
      title: t("engagement.results.balanceSheet.rouAssetCost"),
      meta: currency,
      icon: <Layers className={iconCls} />,
      "data-testid": "bs-card-rou-cost",
      children: movementRows(schedule.rouAssetCost, currency, "bs-card-rou-cost"),
    },
    {
      title: t("engagement.results.balanceSheet.rouAccumulatedDepreciation"),
      meta: currency,
      icon: <TrendingDown className={iconCls} />,
      "data-testid": "bs-card-rou-accdep",
      children: movementRows(
        schedule.rouAccumulatedDepreciation,
        currency,
        "bs-card-rou-accdep",
      ),
    },
    {
      title: t("engagement.results.balanceSheet.rouNetBookValue"),
      meta: currency,
      icon: <Scale className={iconCls} />,
      "data-testid": "bs-card-rou-net",
      children: (
        <AmountRow
          label={t("engagement.results.balanceSheet.rouNetBookValueLine")}
          amount={schedule.npv.rouAssetNet}
          currency={currency}
          emphasize
        />
      ),
    },
    {
      title: t("engagement.results.balanceSheet.leaseLiability"),
      meta: currency,
      icon: <Wallet className={iconCls} />,
      "data-testid": "bs-card-liability",
      children: movementRows(
        schedule.leaseLiability,
        currency,
        "bs-card-liability",
        <>
          <AmountRow
            label={`└─ ${t("engagement.results.balanceSheet.shortTerm")}`}
            amount={schedule.liabilitySplit.closingShortTerm}
            currency={currency}
            indented
            testId="bs-liability-short-term"
          />
          <AmountRow
            label={`└─ ${t("engagement.results.balanceSheet.longTerm")}`}
            amount={schedule.liabilitySplit.closingLongTerm}
            currency={currency}
            indented
            testId="bs-liability-long-term"
          />
        </>,
      ),
    },
    {
      title: t("engagement.results.balanceSheet.npv"),
      meta: currency,
      icon: <Equal className={iconCls} />,
      colSpan: 2,
      "data-testid": "bs-card-npv",
      children: (
        <div className="space-y-1">
          <AmountRow
            label={t("engagement.results.balanceSheet.rouAssetNet")}
            amount={schedule.npv.rouAssetNet}
            currency={currency}
          />
          <AmountRow
            label={t("engagement.results.balanceSheet.leaseLiabilityTotal")}
            amount={schedule.npv.leaseLiabilityTotal}
            currency={currency}
          />
          <AmountRow
            label={t("engagement.results.balanceSheet.netPosition")}
            amount={schedule.npv.netPosition}
            currency={currency}
            emphasize
          />
        </div>
      ),
    },
  ];

  return <BentoGrid items={items} />;
}
